import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  X, Plus, Trash2, Download, Calendar, Users, ChevronDown,
  FileText, Loader2, Save, Check, Edit3, Church, BookOpen,
  UserPlus, Search, GripVertical, ArrowUp, ArrowDown, Eye, EyeOff,
  RotateCcw, Settings2, ArrowUpDown, BookmarkPlus, Bookmark, Upload, Image
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_ICONS = {
  'sunday-service': { emoji: '  ', color: 'bg-amber-100 text-amber-700' },
  'bible-study': { emoji: '  ', color: 'bg-blue-100 text-blue-700' },
  'small-group': { emoji: '  ', color: 'bg-green-100 text-green-700' },
  'prayer-meeting': { emoji: '  ', color: 'bg-purple-100 text-purple-700' },
  'youth-service': { emoji: '  ', color: 'bg-pink-100 text-pink-700' },
  'midweek-service': { emoji: '  ', color: 'bg-teal-100 text-teal-700' },
  'special-event': { emoji: '  ', color: 'bg-orange-100 text-orange-700' },
};

function PersonInput({ value, onChange, members, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = members.filter(m => m.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative">
      <input
        value={value || ''}
        onChange={e => { onChange(e.target.value); setSearch(e.target.value); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={placeholder || 'Type or select...'}
        className="w-full px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent bg-white"
      />
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2DFD8] rounded-lg shadow-lg z-20 max-h-40 overflow-y-auto">
          {filtered.map(m => (
            <button
              key={m.id}
              onMouseDown={e => { e.preventDefault(); onChange(m.name); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#F4F2EC] transition-colors"
            >
              {m.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Scheduler({ onClose, effectiveTier }) {
  const [templates, setTemplates] = useState({});
  const [members, setMembers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('sunday-service');
  const [view, setView] = useState('table');
  const [editingEntry, setEditingEntry] = useState(null);
  const [formFields, setFormFields] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportLayout, setExportLayout] = useState('landscape');
  const [newMemberName, setNewMemberName] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [customTypeName, setCustomTypeName] = useState('');
  const [showCustomType, setShowCustomType] = useState(false);
  const [fieldOrder, setFieldOrder] = useState([]);
  const [hiddenFields, setHiddenFields] = useState([]);
  const [showFieldConfig, setShowFieldConfig] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  // Sorting
  const [sortBy, setSortBy] = useState('date_desc');
  // Saved templates
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showLoadTemplate, setShowLoadTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Logo upload
  const [showLogoUpload, setShowLogoUpload] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // CSV upload
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  // Custom type creation
  const [customTypeFields, setCustomTypeFields] = useState([{ key: '', label: '', type: 'text' }]);

  const isPaidTier = ['pro', 'church'].includes(effectiveTier);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, mRes, eRes, fcRes, stRes, meRes] = await Promise.all([
        axios.get(`${API}/schedule/templates`),
        axios.get(`${API}/schedule/members`),
        axios.get(`${API}/schedule/entries?schedule_type=${activeType}&sort_by=${sortBy}`),
        axios.get(`${API}/schedule/field-config/${activeType}`),
        axios.get(`${API}/schedule/saved-templates?schedule_type=${activeType}`),
        axios.get(`${API}/auth/me`)
      ]);
      setTemplates(tRes.data.templates || {});
      setMembers(mRes.data.members || []);
      setEntries(eRes.data.entries || []);
      setFieldOrder(fcRes.data.field_order || []);
      setHiddenFields(fcRes.data.hidden_fields || []);
      setSavedTemplates(stRes.data.templates || []);
      setLogoUrl(meRes.data.logo_url || '');
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeType, sortBy]);

  useEffect(() => { loadData(); }, [loadData]);

  const currentTemplate = templates[activeType];
  const allFieldDefs = currentTemplate?.fields || [];
  
  const orderedFieldDefs = (() => {
    if (!fieldOrder.length) return allFieldDefs.filter(f => !hiddenFields.includes(f.key));
    const ordered = [];
    for (const key of fieldOrder) {
      if (!hiddenFields.includes(key)) {
        const fd = allFieldDefs.find(f => f.key === key);
        if (fd) ordered.push(fd);
      }
    }
    for (const fd of allFieldDefs) {
      if (!fieldOrder.includes(fd.key) && !hiddenFields.includes(fd.key)) {
        ordered.push(fd);
      }
    }
    return ordered;
  })();

  const saveFieldConfig = async (newOrder, newHidden) => {
    setFieldOrder(newOrder);
    setHiddenFields(newHidden);
    try {
      await axios.put(`${API}/schedule/field-config/${activeType}`, {
        field_order: newOrder,
        hidden_fields: newHidden
      });
    } catch (err) {
      console.error('Failed to save field config');
    }
  };

  const moveFieldUp = (key) => {
    const keys = orderedFieldDefs.map(f => f.key);
    const idx = keys.indexOf(key);
    if (idx <= 0) return;
    [keys[idx - 1], keys[idx]] = [keys[idx], keys[idx - 1]];
    const fullOrder = [...keys, ...hiddenFields.filter(k => !keys.includes(k))];
    saveFieldConfig(fullOrder, hiddenFields);
  };

  const moveFieldDown = (key) => {
    const keys = orderedFieldDefs.map(f => f.key);
    const idx = keys.indexOf(key);
    if (idx < 0 || idx >= keys.length - 1) return;
    [keys[idx], keys[idx + 1]] = [keys[idx + 1], keys[idx]];
    const fullOrder = [...keys, ...hiddenFields.filter(k => !keys.includes(k))];
    saveFieldConfig(fullOrder, hiddenFields);
  };

  const hideField = (key) => {
    const newHidden = [...hiddenFields, key];
    const newOrder = fieldOrder.length ? fieldOrder : allFieldDefs.map(f => f.key);
    saveFieldConfig(newOrder, newHidden);
  };

  const showField = (key) => {
    const newHidden = hiddenFields.filter(k => k !== key);
    const newOrder = fieldOrder.length ? fieldOrder : allFieldDefs.map(f => f.key);
    saveFieldConfig(newOrder, newHidden);
  };

  const resetFieldConfig = () => {
    const defaultOrder = allFieldDefs.map(f => f.key);
    saveFieldConfig(defaultOrder, []);
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (e, targetIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) return;
    const keys = orderedFieldDefs.map(f => f.key);
    const [moved] = keys.splice(dragIdx, 1);
    keys.splice(targetIdx, 0, moved);
    const fullOrder = [...keys, ...hiddenFields.filter(k => !keys.includes(k))];
    saveFieldConfig(fullOrder, hiddenFields);
    setDragIdx(null);
  };

  const resetForm = () => {
    setFormFields({});
    setCustomFields([]);
    setEditingEntry(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        schedule_type: activeType,
        title: templates[activeType]?.name || activeType,
        fields: formFields,
        custom_fields: customFields.filter(cf => cf.label)
      };
      if (editingEntry) {
        await axios.put(`${API}/schedule/entries/${editingEntry}`, payload);
      } else {
        await axios.post(`${API}/schedule/entries`, payload);
      }
      resetForm();
      setView('table');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    setFormFields(entry.fields || {});
    setCustomFields(entry.custom_fields || []);
    setEditingEntry(entry.id);
    setView('form');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule entry?')) return;
    try {
      await axios.delete(`${API}/schedule/entries/${id}`);
      loadData();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const handleAddMember = async () => {
    if (!newMemberName.trim()) return;
    try {
      await axios.post(`${API}/schedule/members`, { name: newMemberName.trim() });
      setNewMemberName('');
      const mRes = await axios.get(`${API}/schedule/members`);
      setMembers(mRes.data.members || []);
    } catch (err) {
      alert('Failed to add member');
    }
  };

  const handleDeleteMember = async (id) => {
    try {
      await axios.delete(`${API}/schedule/members/${id}`);
      const mRes = await axios.get(`${API}/schedule/members`);
      setMembers(mRes.data.members || []);
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const ids = entries.map(e => e.id);
      const visibleKeys = orderedFieldDefs.map(f => f.key);
      const res = await axios.post(`${API}/schedule/export`, {
        entry_ids: ids,
        format,
        schedule_type: activeType,
        field_order: visibleKeys,
        hidden_fields: hiddenFields,
        layout: exportLayout
      }, { responseType: 'blob' });
      
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeType}_schedule.${format === 'docx' ? 'docx' : 'html'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Save as reusable template
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/schedule/saved-templates`, {
        name: templateName.trim(),
        schedule_type: activeType,
        fields: formFields,
        custom_fields: customFields.filter(cf => cf.label)
      });
      setTemplateName('');
      setShowSaveTemplate(false);
      const stRes = await axios.get(`${API}/schedule/saved-templates?schedule_type=${activeType}`);
      setSavedTemplates(stRes.data.templates || []);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Load saved template into form
  const loadTemplate = (tpl) => {
    setFormFields(tpl.fields || {});
    setCustomFields(tpl.custom_fields || []);
    setShowLoadTemplate(false);
    setView('form');
    setEditingEntry(null);
  };

  // Delete saved template
  const deleteSavedTemplate = async (id) => {
    if (!window.confirm('Delete this saved template?')) return;
    try {
      await axios.delete(`${API}/schedule/saved-templates/${id}`);
      const stRes = await axios.get(`${API}/schedule/saved-templates?schedule_type=${activeType}`);
      setSavedTemplates(stRes.data.templates || []);
    } catch (err) {
      alert('Failed to delete template');
    }
  };

  // Logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload an image'); return; }
    if (file.size > 2 * 1024 * 1024) { alert('Max 2MB'); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/auth/upload-logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLogoUrl(res.data.logo_url);
      setShowLogoUpload(false);
    } catch (err) {
      alert('Upload failed');
    } finally {
      setUploadingLogo(false);
    }
  };

  // CSV parsing
  const handleCsvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    setCsvResult(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { alert('CSV must have a header row and at least one data row'); return; }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
        if (row.name) rows.push({ name: row.name, role: row.role || '', email: row.email || '' });
      }
      setCsvPreview(rows);
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = async () => {
    if (!csvPreview.length) return;
    setUploadingCsv(true);
    try {
      const res = await axios.post(`${API}/schedule/members/upload-csv`, { rows: csvPreview });
      setCsvResult(res.data);
      const mRes = await axios.get(`${API}/schedule/members`);
      setMembers(mRes.data.members || []);
      setCsvPreview([]);
      setCsvFile(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingCsv(false);
    }
  };

  // Custom type creation
  const handleCreateCustomType = async () => {
    if (!customTypeName.trim()) { alert('Enter a template name'); return; }
    const validFields = customTypeFields.filter(f => f.label.trim());
    if (!validFields.length) { alert('Add at least one field'); return; }
    try {
      const fields = validFields.map(f => ({
        key: f.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
        label: f.label.trim(),
        type: f.type
      }));
      await axios.post(`${API}/schedule/custom-types`, { name: customTypeName.trim(), fields });
      setShowCustomType(false);
      setCustomTypeName('');
      setCustomTypeFields([{ key: '', label: '', type: 'text' }]);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create custom type');
    }
  };

  const SORT_OPTIONS = [
    { value: 'date_desc', label: 'Date (Newest)' },
    { value: 'date_asc', label: 'Date (Oldest)' },
    { value: 'created_desc', label: 'Recently Added' },
    { value: 'created_asc', label: 'Oldest Added' },
  ];

  if (!isPaidTier) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="scheduler-modal">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
          <Calendar className="w-14 h-14 mx-auto mb-4 text-[#B8860B]" />
          <h2 className="text-xl font-bold text-[#1B3A6B] mb-2">Scheduling</h2>
          <p className="text-[#475569] mb-6">Schedule management is available for Church and Pro plans.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={onClose} className="px-5 py-2.5 border border-[#E2DFD8] rounded-lg text-sm">Close</button>
            <a href="/pricing" className="px-5 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium">Upgrade Plan</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-2 sm:p-4" data-testid="scheduler-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-[#E2DFD8]">
          <div className="flex items-center gap-3">
            {logoUrl && (
              <img src={logoUrl} alt="Church logo" className="h-9 w-9 rounded-lg object-cover border border-[#E2DFD8]" data-testid="scheduler-logo" />
            )}
            <div>
              <h2 className="font-heading text-lg sm:text-xl font-bold text-[#1B3A6B]">Church Schedule</h2>
              <p className="text-xs sm:text-sm text-[#475569]">{currentTemplate?.name || 'Schedule'} · {entries.length} entries</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowLogoUpload(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-[#475569] border border-[#E2DFD8] rounded-lg hover:bg-[#F4F2EC]"
              title="Upload church logo for exports"
              data-testid="upload-logo-btn"
            >
              <Image className="w-4 h-4" /> <span className="hidden sm:inline">Logo</span>
            </button>
            <button onClick={() => setShowMembers(!showMembers)} className="flex items-center gap-1.5 px-3 py-2 text-sm text-[#475569] border border-[#E2DFD8] rounded-lg hover:bg-[#F4F2EC]" data-testid="manage-members-btn">
              <Users className="w-4 h-4" /> <span className="hidden sm:inline">Members</span>
            </button>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg" data-testid="scheduler-close-btn">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Type sidebar */}
          <div className="hidden sm:flex w-52 border-r border-[#E2DFD8] flex-col bg-[#FAFAF8] overflow-y-auto p-3 space-y-1">
            {Object.entries(templates).map(([key, tpl]) => (
              <button
                key={key}
                onClick={() => { setActiveType(key); resetForm(); setView('table'); }}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${
                  activeType === key ? 'bg-[#1B3A6B] text-white' : 'text-[#475569] hover:bg-[#F4F2EC]'
                }`}
                data-testid={`schedule-type-${key}`}
              >
                <Calendar className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{tpl.name}</span>
              </button>
            ))}
            <button
              onClick={() => setShowCustomType(true)}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-[#94A3B8] hover:bg-[#F4F2EC] transition-colors"
              data-testid="schedule-custom-type-btn"
            >
              <Plus className="w-4 h-4" /> Custom Type
            </button>
          </div>

          {/* Main content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Mobile type selector */}
            <div className="sm:hidden px-4 py-2 border-b border-[#E2DFD8]">
              <select
                value={activeType}
                onChange={e => { setActiveType(e.target.value); resetForm(); setView('table'); }}
                className="w-full text-sm border border-[#E2DFD8] rounded-lg px-3 py-2"
                data-testid="schedule-mobile-type"
              >
                {Object.entries(templates).map(([key, tpl]) => (
                  <option key={key} value={key}>{tpl.name}</option>
                ))}
              </select>
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2DFD8] flex-wrap gap-2">
              <div className="flex gap-2">
                <button
                  onClick={() => { setView('table'); resetForm(); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'table' ? 'bg-[#1B3A6B] text-white' : 'bg-white text-[#475569] border border-[#E2DFD8]'}`}
                  data-testid="schedule-view-table"
                >
                  Table
                </button>
                <button
                  onClick={() => setView('form')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'form' ? 'bg-[#1B3A6B] text-white' : 'bg-white text-[#475569] border border-[#E2DFD8]'}`}
                  data-testid="schedule-view-form"
                >
                  {editingEntry ? 'Edit' : 'New Entry'}
                </button>
                {savedTemplates.length > 0 && (
                  <button
                    onClick={() => setShowLoadTemplate(true)}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-[#B8860B] border border-[#E2DFD8] rounded-lg hover:bg-amber-50 transition-colors"
                    data-testid="load-template-btn"
                  >
                    <Bookmark className="w-4 h-4" /> Templates ({savedTemplates.length})
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Sort control */}
                {view === 'table' && (
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value)}
                      className="appearance-none pl-8 pr-6 py-2 text-xs border border-[#E2DFD8] rounded-lg bg-white text-[#475569] focus:ring-1 focus:ring-[#1B3A6B] cursor-pointer"
                      data-testid="sort-select"
                    >
                      {SORT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <ArrowUpDown className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
                  </div>
                )}
                <button
                  onClick={() => setShowFieldConfig(!showFieldConfig)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${showFieldConfig ? 'bg-[#1B3A6B] text-white border-[#1B3A6B]' : 'text-[#475569] border-[#E2DFD8] hover:border-[#1B3A6B] hover:text-[#1B3A6B]'}`}
                  data-testid="schedule-customize-fields"
                >
                  <Settings2 className="w-4 h-4" /> <span className="hidden sm:inline">Fields</span>
                </button>
                {view === 'table' && entries.length > 0 && (
                  <>
                    <select
                      value={exportLayout}
                      onChange={e => setExportLayout(e.target.value)}
                      className="px-2 py-2 text-xs border border-[#E2DFD8] rounded-lg text-[#475569]"
                      data-testid="export-layout-select"
                    >
                      <option value="landscape">Table (Wide)</option>
                      <option value="portrait">Program (Narrow)</option>
                      <option value="two-column">Two-Column (Side Headings)</option>
                    </select>
                    <button
                      onClick={() => handleExport('pdf')}
                      disabled={exporting}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm text-[#475569] border border-[#E2DFD8] rounded-lg hover:border-[#1B3A6B] hover:text-[#1B3A6B] disabled:opacity-50"
                      data-testid="schedule-export-pdf"
                    >
                      <Download className="w-4 h-4" /> PDF
                    </button>
                    <button
                      onClick={() => handleExport('docx')}
                      disabled={exporting}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm text-[#475569] border border-[#E2DFD8] rounded-lg hover:border-[#B8860B] hover:text-[#B8860B] disabled:opacity-50"
                      data-testid="schedule-export-docx"
                    >
                      <FileText className="w-4 h-4" /> Word
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Field Configuration Panel */}
            {showFieldConfig && (
              <div className="px-4 py-3 border-b border-[#E2DFD8] bg-[#FAFAF8]" data-testid="field-config-panel">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-[#0F1A2A]">Customize Fields — drag to reorder, hide/show</span>
                  <button onClick={resetFieldConfig} className="flex items-center gap-1 text-xs text-[#475569] hover:text-[#1B3A6B]" data-testid="reset-fields-btn">
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {orderedFieldDefs.map((fd, idx) => (
                    <div
                      key={fd.key}
                      draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={(e) => handleDragOver(e)}
                      onDrop={(e) => handleDrop(e, idx)}
                      className="flex items-center gap-1 px-2 py-1.5 bg-white border border-[#E2DFD8] rounded-lg text-xs cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow group"
                      data-testid={`field-chip-${fd.key}`}
                    >
                      <GripVertical className="w-3 h-3 text-[#94A3B8]" />
                      <span className="text-[#334155] font-medium">{fd.label}</span>
                      <div className="flex gap-0.5 ml-1">
                        <button onClick={() => moveFieldUp(fd.key)} className="p-0.5 opacity-0 group-hover:opacity-100 hover:text-[#1B3A6B]" title="Move up">
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button onClick={() => moveFieldDown(fd.key)} className="p-0.5 opacity-0 group-hover:opacity-100 hover:text-[#1B3A6B]" title="Move down">
                          <ArrowDown className="w-3 h-3" />
                        </button>
                        <button onClick={() => hideField(fd.key)} className="p-0.5 opacity-0 group-hover:opacity-100 hover:text-red-500" title="Hide field">
                          <EyeOff className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {hiddenFields.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-[#94A3B8]">Hidden:</span>
                    {hiddenFields.map(key => {
                      const fd = allFieldDefs.find(f => f.key === key);
                      if (!fd) return null;
                      return (
                        <button
                          key={key}
                          onClick={() => showField(key)}
                          className="flex items-center gap-1 px-2 py-1 bg-[#F4F2EC] text-[#94A3B8] rounded text-xs hover:text-[#1B3A6B] hover:bg-white border border-transparent hover:border-[#E2DFD8] transition-colors"
                          data-testid={`field-hidden-${key}`}
                        >
                          <Eye className="w-3 h-3" /> {fd.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-auto p-4">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-6 h-6 animate-spin text-[#1B3A6B]" />
                </div>
              ) : view === 'form' ? (
                /* Form View */
                <div className="max-w-2xl mx-auto space-y-4" data-testid="schedule-form">
                  <h3 className="font-semibold text-[#0F1A2A]">
                    {editingEntry ? 'Edit Entry' : `New ${currentTemplate?.name || ''} Entry`}
                  </h3>
                  {orderedFieldDefs.map(fd => (
                    <div key={fd.key}>
                      <label className="block text-sm font-medium text-[#475569] mb-1">
                        {fd.label} {fd.required && <span className="text-red-400">*</span>}
                      </label>
                      {fd.type === 'date' ? (
                        <input
                          type="date"
                          value={formFields[fd.key] || ''}
                          onChange={e => setFormFields(prev => ({ ...prev, [fd.key]: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B]"
                          data-testid={`field-${fd.key}`}
                        />
                      ) : fd.type === 'person' ? (
                        <PersonInput
                          value={formFields[fd.key]}
                          onChange={val => setFormFields(prev => ({ ...prev, [fd.key]: val }))}
                          members={members}
                          placeholder={`Assign ${fd.label.toLowerCase()}...`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={formFields[fd.key] || ''}
                          onChange={e => setFormFields(prev => ({ ...prev, [fd.key]: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B]"
                          placeholder={fd.label}
                          data-testid={`field-${fd.key}`}
                        />
                      )}
                    </div>
                  ))}

                  {/* Custom fields */}
                  <div className="border-t border-[#E2DFD8] pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-[#475569]">Custom Fields</span>
                      <button
                        onClick={() => setCustomFields([...customFields, { label: '', value: '' }])}
                        className="flex items-center gap-1 text-xs text-[#1B3A6B] hover:underline"
                        data-testid="add-custom-field"
                      >
                        <Plus className="w-3 h-3" /> Add Field
                      </button>
                    </div>
                    {customFields.map((cf, i) => (
                      <div key={i} className="flex gap-2 mb-2">
                        <input
                          value={cf.label}
                          onChange={e => {
                            const updated = [...customFields];
                            updated[i].label = e.target.value;
                            setCustomFields(updated);
                          }}
                          placeholder="Field name"
                          className="flex-1 px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg"
                        />
                        <input
                          value={cf.value}
                          onChange={e => {
                            const updated = [...customFields];
                            updated[i].value = e.target.value;
                            setCustomFields(updated);
                          }}
                          placeholder="Value"
                          className="flex-1 px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg"
                        />
                        <button
                          onClick={() => setCustomFields(customFields.filter((_, j) => j !== i))}
                          className="p-2 text-[#94A3B8] hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3 pt-2 flex-wrap">
                    <button
                      onClick={handleSave}
                      disabled={saving || !formFields.date}
                      className="flex items-center gap-2 px-6 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] disabled:opacity-50"
                      data-testid="schedule-save-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {editingEntry ? 'Update' : 'Save Entry'}
                    </button>
                    <button
                      onClick={() => setShowSaveTemplate(true)}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm text-[#B8860B] border border-[#E2DFD8] rounded-lg hover:bg-amber-50 transition-colors"
                      data-testid="save-as-template-btn"
                    >
                      <BookmarkPlus className="w-4 h-4" /> Save as Template
                    </button>
                    <button
                      onClick={() => { resetForm(); setView('table'); }}
                      className="px-5 py-2.5 text-sm text-[#475569] border border-[#E2DFD8] rounded-lg hover:bg-[#F4F2EC]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* Table View */
                entries.length === 0 ? (
                  <div className="text-center py-16">
                    <Calendar className="w-14 h-14 mx-auto mb-4 text-[#D1CBC0]" />
                    <p className="text-[#475569] font-medium mb-1">No {currentTemplate?.name} entries yet</p>
                    <p className="text-sm text-[#94A3B8] mb-4">Create your first schedule entry</p>
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => setView('form')}
                        className="px-5 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium"
                        data-testid="schedule-create-first"
                      >
                        Create Entry
                      </button>
                      {savedTemplates.length > 0 && (
                        <button
                          onClick={() => setShowLoadTemplate(true)}
                          className="flex items-center gap-1.5 px-5 py-2.5 text-sm text-[#B8860B] border border-[#E2DFD8] rounded-lg hover:bg-amber-50"
                          data-testid="load-template-empty-btn"
                        >
                          <Bookmark className="w-4 h-4" /> From Template
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg" data-testid="schedule-table">
                    <table className="clean-table">
                      <thead>
                        <tr>
                          {orderedFieldDefs.map(fd => (
                            <th key={fd.key}>
                              {fd.label}
                            </th>
                          ))}
                          {entries[0]?.custom_fields?.filter(cf => cf.label).map((cf, i) => (
                            <th key={`custom-${i}`}>
                              {cf.label}
                            </th>
                          ))}
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry, idx) => (
                          <tr key={entry.id}>
                            {orderedFieldDefs.map(fd => (
                              <td key={fd.key}>
                                {fd.type === 'date' && entry.fields?.[fd.key]
                                  ? new Date(entry.fields[fd.key] + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                                  : entry.fields?.[fd.key] || '-'}
                              </td>
                            ))}
                            {entry.custom_fields?.filter(cf => cf.label).map((cf, i) => (
                              <td key={`custom-${i}`}>
                                {cf.value || '-'}
                              </td>
                            ))}
                            <td className="text-right">
                              <div className="flex gap-1 justify-end">
                                <button onClick={() => handleEdit(entry)} className="p-1.5 hover:bg-[#F4F2EC] rounded" title="Edit">
                                  <Edit3 className="w-3.5 h-3.5 text-[#475569]" />
                                </button>
                                <button onClick={() => handleDelete(entry.id)} className="p-1.5 hover:bg-red-50 rounded" title="Delete">
                                  <Trash2 className="w-3.5 h-3.5 text-[#475569] hover:text-red-500" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Members Panel */}
        {showMembers && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-10">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" data-testid="members-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#0F1A2A]">Church Members</h3>
                <button onClick={() => setShowMembers(false)}><X className="w-5 h-5 text-[#475569]" /></button>
              </div>
              <p className="text-xs text-[#94A3B8] mb-3">Add names to quickly assign people in schedules</p>
              
              {/* Add single member */}
              <div className="flex gap-2 mb-3">
                <input
                  value={newMemberName}
                  onChange={e => setNewMemberName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddMember()}
                  placeholder="Member name..."
                  className="flex-1 px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg"
                  data-testid="new-member-input"
                />
                <button
                  onClick={handleAddMember}
                  className="px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm"
                  data-testid="add-member-btn"
                >
                  <UserPlus className="w-4 h-4" />
                </button>
              </div>

              {/* CSV Upload Toggle */}
              <button
                onClick={() => { setShowCsvUpload(!showCsvUpload); setCsvResult(null); setCsvPreview([]); setCsvFile(null); }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-3 text-sm text-[#B8860B] border border-dashed border-[#B8860B]/40 rounded-lg hover:bg-amber-50 transition-colors"
                data-testid="csv-upload-toggle-btn"
              >
                <Upload className="w-4 h-4" /> Bulk Upload via CSV
              </button>

              {/* CSV Upload Section */}
              {showCsvUpload && (
                <div className="mb-4 p-3 bg-[#FAFAF8] rounded-lg border border-[#E2DFD8]" data-testid="csv-upload-section">
                  <p className="text-xs text-[#475569] mb-2">Upload a CSV file with columns: <strong>Name, Role, Email</strong></p>
                  <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-[#E2DFD8] rounded-lg cursor-pointer hover:border-[#1B3A6B] hover:bg-white transition-colors mb-2">
                    <Upload className="w-5 h-5 text-[#94A3B8] mb-1" />
                    <span className="text-xs text-[#475569]">{csvFile ? csvFile.name : 'Click to select CSV'}</span>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCsvFileSelect}
                      className="hidden"
                      data-testid="csv-file-input"
                    />
                  </label>

                  {/* CSV Preview */}
                  {csvPreview.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-medium text-[#0F1A2A] mb-1">{csvPreview.length} member{csvPreview.length !== 1 ? 's' : ''} found:</p>
                      <div className="max-h-32 overflow-y-auto border border-[#E2DFD8] rounded-lg">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-[#F4F2EC]">
                              <th className="px-2 py-1.5 text-left text-[#475569]">Name</th>
                              <th className="px-2 py-1.5 text-left text-[#475569]">Role</th>
                              <th className="px-2 py-1.5 text-left text-[#475569]">Email</th>
                            </tr>
                          </thead>
                          <tbody>
                            {csvPreview.slice(0, 10).map((row, i) => (
                              <tr key={i} className="border-t border-[#E2DFD8]">
                                <td className="px-2 py-1 text-[#334155]">{row.name}</td>
                                <td className="px-2 py-1 text-[#334155]">{row.role}</td>
                                <td className="px-2 py-1 text-[#334155]">{row.email}</td>
                              </tr>
                            ))}
                            {csvPreview.length > 10 && (
                              <tr><td colSpan="3" className="px-2 py-1 text-center text-[#94A3B8]">...and {csvPreview.length - 10} more</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <button
                        onClick={handleCsvUpload}
                        disabled={uploadingCsv}
                        className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] disabled:opacity-50"
                        data-testid="csv-upload-confirm-btn"
                      >
                        {uploadingCsv ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Import {csvPreview.length} Members
                      </button>
                    </div>
                  )}

                  {/* CSV Result */}
                  {csvResult && (
                    <div className="p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700" data-testid="csv-upload-result">
                      <Check className="w-3.5 h-3.5 inline mr-1" />
                      Added {csvResult.added} member{csvResult.added !== 1 ? 's' : ''}{csvResult.skipped > 0 ? `, ${csvResult.skipped} skipped (no name)` : ''}
                    </div>
                  )}
                </div>
              )}

              <div className="max-h-60 overflow-y-auto space-y-1">
                {members.length === 0 ? (
                  <p className="text-sm text-[#94A3B8] text-center py-4">No members added yet</p>
                ) : members.map(m => (
                  <div key={m.id} className="flex items-center justify-between px-3 py-2 hover:bg-[#F4F2EC] rounded-lg group">
                    <div>
                      <span className="text-sm text-[#334155]">{m.name}</span>
                      {m.role && <span className="ml-2 text-xs text-[#94A3B8]">{m.role}</span>}
                    </div>
                    <button
                      onClick={() => handleDeleteMember(m.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-500"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#94A3B8] mt-3">{members.length} members</p>
            </div>
          </div>
        )}

        {/* Save as Template Modal */}
        {showSaveTemplate && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-10">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4" data-testid="save-template-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#0F1A2A]">Save as Template</h3>
                <button onClick={() => setShowSaveTemplate(false)}><X className="w-5 h-5 text-[#475569]" /></button>
              </div>
              <p className="text-xs text-[#94A3B8] mb-3">Save the current form fields as a reusable template for {currentTemplate?.name}</p>
              <input
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveAsTemplate()}
                placeholder="Template name (e.g., Default Sunday Setup)"
                className="w-full px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] mb-4"
                data-testid="template-name-input"
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowSaveTemplate(false)} className="px-4 py-2 text-sm text-[#475569] border border-[#E2DFD8] rounded-lg hover:bg-[#F4F2EC]">Cancel</button>
                <button
                  onClick={handleSaveAsTemplate}
                  disabled={!templateName.trim() || savingTemplate}
                  className="flex items-center gap-2 px-5 py-2 bg-[#B8860B] text-white rounded-lg text-sm font-medium hover:bg-[#9A7209] disabled:opacity-50"
                  data-testid="confirm-save-template-btn"
                >
                  {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4" />}
                  Save Template
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Load Template Modal */}
        {showLoadTemplate && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-10">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" data-testid="load-template-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#0F1A2A]">Saved Templates</h3>
                <button onClick={() => setShowLoadTemplate(false)}><X className="w-5 h-5 text-[#475569]" /></button>
              </div>
              <p className="text-xs text-[#94A3B8] mb-3">Select a template to pre-fill the form</p>
              <div className="max-h-72 overflow-y-auto space-y-2">
                {savedTemplates.length === 0 ? (
                  <p className="text-sm text-[#94A3B8] text-center py-8">No saved templates for {currentTemplate?.name}</p>
                ) : savedTemplates.map(tpl => (
                  <div key={tpl.id} className="flex items-center justify-between px-4 py-3 border border-[#E2DFD8] rounded-lg hover:border-[#1B3A6B] hover:bg-[#F4F2EC] transition-colors group cursor-pointer" data-testid={`saved-template-${tpl.id}`}>
                    <button onClick={() => loadTemplate(tpl)} className="flex-1 text-left">
                      <p className="text-sm font-medium text-[#0F1A2A]">{tpl.name}</p>
                      <p className="text-xs text-[#94A3B8]">
                        {new Date(tpl.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {tpl.fields?.date ? ` · Date: ${tpl.fields.date}` : ''}
                      </p>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteSavedTemplate(tpl.id); }}
                      className="opacity-0 group-hover:opacity-100 p-1.5 hover:text-red-500 text-[#94A3B8] transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Custom Schedule Type Modal */}
        {showCustomType && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-10">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" data-testid="custom-type-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#0F1A2A]">Create Custom Schedule Type</h3>
                <button onClick={() => setShowCustomType(false)}><X className="w-5 h-5 text-[#475569]" /></button>
              </div>
              <p className="text-xs text-[#94A3B8] mb-4">Define your own schedule structure with custom fields</p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#475569] mb-1">Template Name *</label>
                <input
                  value={customTypeName}
                  onChange={e => setCustomTypeName(e.target.value)}
                  placeholder="e.g., Choir Practice, Outreach Event"
                  className="w-full px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B]"
                  data-testid="custom-type-name-input"
                />
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-[#475569]">Fields *</label>
                  <button
                    onClick={() => setCustomTypeFields([...customTypeFields, { key: '', label: '', type: 'text' }])}
                    className="flex items-center gap-1 text-xs text-[#1B3A6B] hover:underline"
                    data-testid="add-type-field-btn"
                  >
                    <Plus className="w-3 h-3" /> Add Field
                  </button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {customTypeFields.map((field, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        value={field.label}
                        onChange={e => {
                          const updated = [...customTypeFields];
                          updated[i].label = e.target.value;
                          setCustomTypeFields(updated);
                        }}
                        placeholder="Field name"
                        className="flex-1 px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg"
                        data-testid={`type-field-label-${i}`}
                      />
                      <select
                        value={field.type}
                        onChange={e => {
                          const updated = [...customTypeFields];
                          updated[i].type = e.target.value;
                          setCustomTypeFields(updated);
                        }}
                        className="w-24 px-2 py-2 text-sm border border-[#E2DFD8] rounded-lg"
                        data-testid={`type-field-type-${i}`}
                      >
                        <option value="text">Text</option>
                        <option value="person">Person</option>
                        <option value="date">Date</option>
                      </select>
                      {customTypeFields.length > 1 && (
                        <button
                          onClick={() => setCustomTypeFields(customTypeFields.filter((_, j) => j !== i))}
                          className="p-1.5 text-[#94A3B8] hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowCustomType(false)} className="px-4 py-2 text-sm text-[#475569] border border-[#E2DFD8] rounded-lg hover:bg-[#F4F2EC]">Cancel</button>
                <button
                  onClick={handleCreateCustomType}
                  disabled={!customTypeName.trim() || !customTypeFields.some(f => f.label.trim())}
                  className="flex items-center gap-2 px-5 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] disabled:opacity-50"
                  data-testid="create-custom-type-btn"
                >
                  <Plus className="w-4 h-4" /> Create Template
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logo Upload Modal */}
        {showLogoUpload && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-10">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4" data-testid="logo-upload-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#0F1A2A]">Church Logo</h3>
                <button onClick={() => setShowLogoUpload(false)}><X className="w-5 h-5 text-[#475569]" /></button>
              </div>
              <p className="text-xs text-[#94A3B8] mb-4">Upload your church logo. It will appear on exported schedules.</p>
              
              {logoUrl && (
                <div className="flex justify-center mb-4">
                  <img src={logoUrl} alt="Current logo" className="h-20 w-20 object-contain rounded-lg border border-[#E2DFD8]" data-testid="current-logo-preview" />
                </div>
              )}
              
              <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-[#E2DFD8] rounded-xl cursor-pointer hover:border-[#1B3A6B] hover:bg-[#F4F2EC] transition-colors">
                {uploadingLogo ? (
                  <Loader2 className="w-6 h-6 animate-spin text-[#1B3A6B]" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-[#94A3B8] mb-1" />
                    <span className="text-sm text-[#475569]">Click to upload</span>
                    <span className="text-xs text-[#94A3B8]">PNG, JPG up to 2MB</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-file-input"
                />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
